import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import api from '../services/api';

const QuickTerminateConfirm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [checked, setChecked] = useState(false);

  // If already confirmed in this browser, jump straight to success
  useEffect(() => {
    if (token && localStorage.getItem(`qt_confirmed_${token}`)) {
      setStatus('success');
    }
  }, [token]);

  const handleSubmit = async () => {
    if (!token || !checked) return;
    setStatus('loading');
    try {
      await api.post(`/offboarding/token/${token}/confirm-access-revoked`);
      localStorage.setItem(`qt_confirmed_${token}`, '1');
      setStatus('success');
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'This link is invalid or has expired. Please contact HR.';
      setErrorMessage(detail);
      setStatus('error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F9FAFB',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 480,
          width: '100%',
          p: 5,
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        {status === 'idle' && (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <Typography sx={{ fontSize: 32 }}>🔐</Typography>
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Confirm Access Revocation
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Please confirm that you have revoked all system access (email, VPN, accounts) for this employee.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Typography variant="body2" fontWeight={500} textAlign="left">
                  I confirm I have revoked all system access for this employee
                </Typography>
              }
              sx={{ mb: 3, textAlign: 'left', alignItems: 'flex-start' }}
            />

            <Button
              variant="contained"
              color="error"
              size="large"
              fullWidth
              disabled={!checked}
              onClick={handleSubmit}
              sx={{ py: 1.5, fontWeight: 600, borderRadius: 2 }}
            >
              Submit Confirmation
            </Button>
          </>
        )}

        {status === 'loading' && (
          <>
            <CircularProgress size={48} sx={{ mb: 3 }} />
            <Typography variant="body1" color="text.secondary">
              Submitting...
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom color="success.main">
              Confirmed!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Access revocation confirmed. HR has been notified automatically. Thank you.
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom color="error.main">
              Invalid Link
            </Typography>
            <Alert severity="error" sx={{ textAlign: 'left', mt: 1 }}>
              {errorMessage}
            </Alert>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default QuickTerminateConfirm;
